"use client"
import Image from "next/image"
import { useMemo, useState } from "react"
import type { UserView } from "../lib/user-view.ts"
import { CatalogBrandNavigation } from "./catalog-brand-navigation.tsx"
import { CatalogSortControl, type CatalogSortOrder } from "./catalog-sort-control.tsx"
import { ConsumerProductCard } from "./consumer-product-card.tsx"
import { useConsumerSessionMemory } from "./consumer-session.tsx"
import { EmptyState } from "./empty-state.tsx"
import { PointAmount } from "./point-amount.tsx"
import { PromoBanner } from "./promo-banner.tsx"

type CatalogViewModel = Extract<
  UserView,
  {
    readonly kind: "catalog"
  }
>
type CatalogFilters = {
  readonly affordableOnly?: boolean
  readonly availableWon?: number | undefined
  readonly brand: string
  readonly query: string
  readonly sellableOnly: boolean
  readonly sort: CatalogSortOrder
}
export const selectCatalogProducts = (products: CatalogViewModel["products"], filters: CatalogFilters): CatalogViewModel["products"] => {
  const normalized = filters.query.trim().toLocaleLowerCase("ko-KR")
  return products
    .filter((product) => {
      const brand = product.brandName ?? ""
      const matchesQuery = normalized.length === 0 || `${brand} ${product.name}`.toLocaleLowerCase("ko-KR").includes(normalized)
      const affordable = !filters.affordableOnly || (filters.availableWon !== undefined && product.available && product.priceWon <= filters.availableWon)
      return matchesQuery && (filters.brand === "all" || brand === filters.brand) && (!filters.sellableOnly || product.available) && affordable
    })
    .toSorted((left, right) => (filters.sort === "price-asc" ? left.priceWon - right.priceWon : filters.sort === "price-desc" ? right.priceWon - left.priceWon : 0))
}
export const CatalogView = ({ view }: { readonly view: CatalogViewModel }) => {
  const memory = useConsumerSessionMemory()
  const [affordableOnly, setAffordableOnlyState] = useState(() => memory?.catalog.affordableOnly ?? false)
  const setAffordableOnly = (value: boolean): void => {
    if (memory !== null) memory.catalog.affordableOnly = value
    setAffordableOnlyState(value)
  }
  const [brand, setBrandState] = useState(() => memory?.catalog.brand ?? "all")
  const [query, setQueryState] = useState(() => memory?.catalog.query ?? "")
  const [sort, setSortState] = useState<CatalogSortOrder>(() => memory?.catalog.sort ?? "recommended")
  const setBrand = (value: string): void => {
    if (memory !== null) memory.catalog.brand = value
    setBrandState(value)
  }
  const setQuery = (value: string): void => {
    if (memory !== null) memory.catalog.query = value
    setQueryState(value)
  }
  const setSort = (value: CatalogSortOrder): void => {
    if (memory !== null) memory.catalog.sort = value
    setSortState(value)
  }
  const brands = useMemo(() => [...new Set(view.products.map((product) => product.brandName ?? "").filter((value) => value.length > 0))], [view.products])
  const products = useMemo(() => selectCatalogProducts(view.products, { affordableOnly, availableWon: view.availableWon, brand, query, sellableOnly: false, sort }), [affordableOnly, brand, query, sort, view.availableWon, view.products])
  const promo = view.promoBanner ?? null
  const hasQuery = query.trim().length > 0
  if (view.products.length === 0)
    return (
      <div className="catalog-layout catalog-layout-redesign" data-commerce-catalog>
        {promo !== null ? <PromoBanner banner={promo} /> : null}
        <EmptyState description="교환 가능한 상품이 등록되면 이곳에서 확인할 수 있어요." illustration="catalog-pending" title="상품을 준비하고 있어요" />
      </div>
    )
  return (
    <div className="catalog-layout catalog-layout-commerce catalog-layout-redesign" data-commerce-catalog>
      {promo !== null ? <PromoBanner banner={promo} /> : null}
      <label className="catalog-search-commerce">
        <span className="visually-hidden">상품 검색</span>
        <span aria-hidden="true" className="catalog-search-icon">
          {<Image alt="" height={16} src="/assets/cashhub/redesign/shop-search.svg" width={16} />}
        </span>
        <input autoComplete="off" enterKeyHint="search" inputMode="search" onChange={(event) => setQuery(event.target.value)} placeholder="상품권·브랜드 검색" type="search" value={query} />
      </label>
      {brands.length > 0 ? <CatalogBrandNavigation brands={brands} onChange={setBrand} value={brand} /> : null}
      <label className="catalog-affordable-filter">
        <input checked={affordableOnly} disabled={view.availableWon === undefined} onChange={(event) => setAffordableOnly(event.target.checked)} type="checkbox" />
        <span>내 포인트로 구매 가능</span>
        {view.availableWon !== undefined ? (
          <small>
            <PointAmount amount={view.availableWon} /> 보유
          </small>
        ) : (
          <small>포인트 확인 중</small>
        )}
      </label>
      <div className="catalog-sort-row" data-vivid-catalog-toolbar>
        <p>
          <strong>{products.length}</strong>개의 상품
        </p>
        <CatalogSortControl onChange={setSort} value={sort} />
      </div>
      {products.length === 0 ? (
        <EmptyState description={affordableOnly ? "포인트 필터를 끄거나 다른 브랜드를 선택해 보세요." : hasQuery ? "검색어나 분류를 바꿔 다시 찾아보세요." : "다른 분류를 선택하면 상품을 확인할 수 있어요."} illustration="catalog-pending" title={hasQuery ? "검색 결과가 없어요" : "조건에 맞는 상품이 없어요"} />
      ) : (
        <section aria-label="상품 목록" className="consumer-product-grid product-grid-commerce">
          {products.map((product) => (
            <ConsumerProductCard key={product.id} product={product} />
          ))}
        </section>
      )}
    </div>
  )
}
